import { useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { useAuth0 } from '../auth';
import { AUTH0_CUSTOM_CLAIMS_NAMESPACE } from '../config';
import {
  UserContext,
  type UserInfo,
  type UserContextValue,
} from './UserContextDefinition';

export function UserProvider({ children }: PropsWithChildren) {
  const { user, isLoading, isAuthenticated } = useAuth0();

  const userInfo = useMemo<UserInfo | null>(() => {
    if (!isAuthenticated || !user) {
      return null;
    }

    // Extract locusUsername from custom claims in ID token
    const customClaims = user[AUTH0_CUSTOM_CLAIMS_NAMESPACE] as
      | Record<string, unknown>
      | undefined;
    const locusUsername = customClaims?.locusUsername as string | undefined;

    // Use locusUsername as the primary userId, with fallbacks
    const userId =
      locusUsername || user.sub || user.user_id || user.email || 'unknown';

    return {
      userId,
      email: user.email,
      name: user.name,
      picture: user.picture,
      // Include custom claims for additional context
      clientId: customClaims?.clientId as string | undefined,
      homeTenant: customClaims?.homeTenant as string | undefined,
      roleId: customClaims?.roleId as string | undefined,
      targetTenant: customClaims?.targetTenant as string | undefined,
      ...user, // Include any additional user properties
    };
  }, [user, isAuthenticated]);

  const contextValue = useMemo<UserContextValue>(
    () => ({
      user: userInfo,
      isLoading,
    }),
    [userInfo, isLoading]
  );

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
}
